import React from 'react';
import { motion } from 'framer-motion';
import publicData from '../../../data/publicData.json';

const ServicesSection = ({ theme = 'dark' }) => {
  // Invert the theme logic - when theme is dark, we want light appearance and vice versa
  const isLight = theme === 'dark';
  const { services } = publicData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="services" 
      className={`py-16 ${
        isLight 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div 
        className="container mx-auto px-4 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.h2 
          className={`text-4xl font-bold mb-8 ${
            isLight ? 'text-white' : 'text-gray-900'
          }`}
          variants={itemVariants}
        >
          {services.title}
        </motion.h2>
        <motion.p 
          className={`text-lg mb-12 max-w-3xl mx-auto ${
            isLight ? 'text-gray-300' : 'text-gray-600'
          }`}
          variants={itemVariants}
        >
          {services.description}
        </motion.p>
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
        >
          {services.items.map((service, index) => (
            <motion.div
              key={index}
              className={`p-6 rounded-xl transition-all duration-300 ${
                isLight
                  ? 'hover:bg-blue-900/10 bg-gray-900/50 backdrop-blur-sm'
                  : 'hover:bg-blue-50/50 bg-white/50 backdrop-blur-sm shadow-lg'
              }`}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
            >
              <div className={`p-4 rounded-lg mb-4 inline-block ${
                isLight ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <svg 
                  className={`w-10 h-10 ${isLight ? 'text-blue-400' : 'text-blue-600'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d={service.icon} 
                  />
                </svg>
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${
                isLight ? 'text-white' : 'text-gray-900'
              }`}>
                {service.name}
              </h3>
              <p className={`mb-4 ${
                isLight ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {service.description}
              </p>
              <ul className={`text-sm space-y-2 text-left ${
                isLight ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center">
                    <span className={`mr-2 ${
                      isLight ? 'text-blue-400' : 'text-blue-600'
                    }`}>•</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ServicesSection;
